import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatPhone, digitsOnly } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClinic } from "@/context/ClinicContext";
import StatusBadge from "@/components/StatusBadge";
import { Search, SearchX, Plus, UserPlus, X, Loader2 } from "lucide-react";
import EmptyState from "@/components/EmptyState";

const FALLBACK_PROGRAMS = ["TRT", "Weight Loss", "Peptides", "IV Therapy", "Functional Medicine", "Concierge", "Longevity"];
const STATUSES = ["active", "paused", "inactive"];

function NewPatientModal({ programs, onClose, onCreated }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", dob: "", sex: "M", email: "", phone: "",
    program: programs[0] || "TRT", status: "active", membership: "monthly", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      // Digits go to the server; the pretty form is only ever for reading.
      const { data } = await api.post("/patients", { ...form, phone: digitsOnly(form.phone) });
      onCreated(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="new-patient-modal">
      <div className="bg-card rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card">
          <h3 className="font-heading text-lg font-semibold">New Patient</h3>
          <button data-testid="close-modal" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="First name"><input data-testid="patient-first-name" required value={form.first_name} onChange={set("first_name")} className="input" /></Field>
            <Field label="Last name"><input data-testid="patient-last-name" required value={form.last_name} onChange={set("last_name")} className="input" /></Field>
            <Field label="Date of birth"><input data-testid="patient-dob" type="date" required value={form.dob} onChange={set("dob")} className="input" /></Field>
            <Field label="Sex">
              <select data-testid="patient-sex" value={form.sex} onChange={set("sex")} className="input">
                <option value="M">Male</option><option value="F">Female</option><option value="O">Other</option>
              </select>
            </Field>
            <Field label="Email"><input data-testid="patient-email" type="email" value={form.email} onChange={set("email")} className="input" /></Field>
            <Field label="Phone">
              {/* Tidied on blur, not per keystroke: reformatting mid-typing fights the
                  caret and makes correcting a digit unpleasant. */}
              <input data-testid="patient-phone" value={form.phone} onChange={set("phone")}
                     onBlur={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))}
                     inputMode="tel" placeholder="(555) 555-5555" className="input" />
            </Field>
            <Field label="Program">
              <select data-testid="patient-program" value={form.program} onChange={set("program")} className="input">
                {programs.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select data-testid="patient-status" value={form.status} onChange={set("status")} className="input">
                {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </Field>
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm font-medium hover:bg-secondary">Cancel</button>
            <button data-testid="save-patient-button" type="submit" disabled={saving} className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Create patient
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { clinic } = useClinic();
  const programs = (clinic?.programs?.length ? clinic.programs : FALLBACK_PROGRAMS);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    const { data } = await api.get("/patients", { params });
    setPatients(data);
    setLoading(false);
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="patients-page">
      <style>{`.input{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));border-radius:0.375rem;padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.input:focus{box-shadow:0 0 0 2px hsl(var(--ring))}`}</style>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Patients</h1>
          <p className="text-sm text-muted-foreground mt-1">{patients.length} records in this clinic.</p>
        </div>
        <button
          data-testid="new-patient-button"
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-all"
        >
          <UserPlus className="w-4 h-4" /> New Patient
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid="patient-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full rounded-md border border-input bg-card pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <select
          data-testid="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/50">
              <th className="text-left font-medium text-muted-foreground px-4 py-3">Patient</th>
              <th className="text-left font-medium text-muted-foreground px-4 py-3 hidden md:table-cell">Program</th>
              <th className="text-left font-medium text-muted-foreground px-4 py-3 hidden lg:table-cell">DOB</th>
              <th className="text-left font-medium text-muted-foreground px-4 py-3">Status</th>
              <th className="text-left font-medium text-muted-foreground px-4 py-3 hidden lg:table-cell">Email</th>
            </tr>
          </thead>
          <tbody data-testid="patients-table-body">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b border-border last:border-0">
                  <td className="px-4 py-3.5"><div className="h-4 w-36 bg-secondary rounded animate-pulse" /></td>
                  <td className="px-4 py-3.5 hidden md:table-cell"><div className="h-4 w-20 bg-secondary rounded animate-pulse" /></td>
                  <td className="px-4 py-3.5 hidden lg:table-cell"><div className="h-4 w-24 bg-secondary rounded animate-pulse" /></td>
                  <td className="px-4 py-3.5"><div className="h-5 w-16 bg-secondary rounded-full animate-pulse" /></td>
                  <td className="px-4 py-3.5 hidden lg:table-cell"><div className="h-4 w-40 bg-secondary rounded animate-pulse" /></td>
                </tr>
              ))
            ) : patients.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState
                    testid="patients-empty"
                    icon={search || statusFilter ? SearchX : UserPlus}
                    title={search || statusFilter ? "No patients match your filters" : "No patients yet"}
                    description={search || statusFilter ? "Try adjusting your search or status filter." : "Add your first patient to start building the relationship."}
                    action={!search && !statusFilter && (
                      <button data-testid="empty-new-patient" onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90">
                        <UserPlus className="w-4 h-4" /> New Patient
                      </button>
                    )}
                  />
                </td>
              </tr>
            ) : (
              patients.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`patient-row-${p.id}`}
                  onClick={() => navigate(`/patients/${p.id}`)}
                  className="border-b border-border last:border-0 hover:bg-secondary/40 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 font-medium">{p.first_name} {p.last_name}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{p.program}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground tabular-nums">{p.dob}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground truncate max-w-[200px]">{p.email}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <NewPatientModal
          programs={programs}
          onClose={() => setShowModal(false)}
          onCreated={(p) => { setShowModal(false); navigate(`/patients/${p.id}`); }}
        />
      )}
    </div>
  );
}
